import React, { useState } from 'react';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Button from 'react-bootstrap/Button';
import { useJobs } from '../contexts/JobsContext';
import { getFileNameFromHeaders, saveBlobAsFile, downloadWithRetry } from '../utils/downloadFile';

/**
 * Persistent, app-wide progress notifications for background jobs. Mounted
 * once in App.jsx outside <Routes>, so it stays visible across navigation —
 * unlike the old per-screen inline progress bars it replaces.
 */
const JobsToastTray = () => {
  const { jobs, dismissJob, getDownloader } = useJobs();
  const [downloadState, setDownloadState] = useState({}); // jobId -> { busy, percent }

  if (jobs.length === 0) return null;

  const handleDownload = async (job) => {
    const download = getDownloader(job.jobId);
    setDownloadState((prev) => ({ ...prev, [job.jobId]: { busy: true, percent: null } }));

    try {
      const response = await downloadWithRetry(download, {
        onProgress: (progressEvent) => {
          if (!progressEvent?.total) return;
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setDownloadState((prev) => ({ ...prev, [job.jobId]: { busy: true, percent } }));
        }
      });
      saveBlobAsFile(response.data, getFileNameFromHeaders(response.headers, job.fileName || job.label || 'download'));
    } catch (error) {
      console.error('[JobsToastTray] Download failed:', error);
    }

    setDownloadState((prev) => ({ ...prev, [job.jobId]: { busy: false, percent: null } }));
  };

  return (
    <ToastContainer position="bottom-end" className="p-3" style={{ position: 'fixed', zIndex: 1080 }}>
      {jobs.map((job) => {
        const download = downloadState[job.jobId] || {};
        const isTerminal = job.status === 'completed' || job.status === 'failed';
        return (
          <Toast
            key={job.jobId}
            // dismissJob stops tracking the job entirely (clears its polling,
            // onComplete, and persisted entry) — closing the toast on a
            // still-running job would silently drop the backend work from
            // tracking and, for jobs like the postcode lookup, skip the
            // completion callback the page is waiting on. Only let a
            // finished (completed/failed) job actually be dismissed.
            onClose={() => { if (isTerminal) dismissJob(job.jobId); }}
          >
            <Toast.Header closeButton={isTerminal}>
              <strong className="me-auto">{job.label || 'Background task'}</strong>
              <small className="text-muted text-capitalize">{job.status}</small>
            </Toast.Header>
            <Toast.Body>
              {job.status === 'failed' ? (
                <div className="text-danger small">{job.error || 'This job failed.'}</div>
              ) : job.status === 'completed' ? (
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <span className="small text-success">{job.message || 'Complete.'}</span>
                  {job.downloadable && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={download.busy}
                      onClick={() => handleDownload(job)}
                    >
                      {download.busy ? (download.percent != null ? `Downloading… ${download.percent}%` : 'Downloading…') : 'Download'}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="small text-muted mb-1">{job.message || 'Working…'}</div>
                  <ProgressBar
                    now={job.percent || 0}
                    label={`${job.percent || 0}%`}
                    animated
                    striped
                  />
                </>
              )}
            </Toast.Body>
          </Toast>
        );
      })}
    </ToastContainer>
  );
};

export default JobsToastTray;
